/**
 * mockResumes.js
 * Tailored mock resume datasets matching the styles and names
 * from the user's reference images to render realistic previews in the Template Gallery.
 */

export const MOCK_RESUMES = {
  // Avery Davis - Data Analyst
  harvard_classic: {
    name: "Avery Davis",
    role: "Data Analyst",
    email: "avery.davis@email.com",
    phone: "+1 (555) 019-2834",
    city: "New York, NY",
    summary: "Detail-oriented and results-driven Data Analyst with 5+ years of experience conducting advanced database queries, building predictive models, and translating complex data metrics into actionable business strategies.",
    skills: ["SQL (PostgreSQL, BigQuery)", "Python (Pandas, NumPy)", "Tableau", "Power BI", "Excel (VBA)", "ETL Pipelines", "Statistical Analysis", "Data Modeling"],
    experience: [
      {
        company: "Salford & Co.",
        role: "Data Analyst",
        duration: "2020 – Present",
        location: "New York, NY",
        points: [
          "Analyzed customer behavior datasets to optimize marketing campaigns, boosting conversion rate by 18%.",
          "Developed automated reporting dashboards using Tableau, saving 15 hours of manual work weekly.",
          "Conducted statistical A/B testing on product features, leading to a 5% increase in user retention.",
        ],
      },
      {
        company: "Thynk Unlimited",
        role: "Junior Data Analyst",
        duration: "2018 – 2020",
        location: "Boston, MA",
        points: [
          "Compiled monthly sales performance reports and presented insights to regional managers.",
          "Cleaned and preprocessed raw structured/unstructured datasets, improving data quality metrics by 25%.",
        ],
      },
    ],
    education: [
      {
        college: "Boston University",
        degree: "B.S. in Computer Science & Statistics",
        year: "2014 - 2017",
        cgpa: "3.8 GPA",
      },
    ],
    projects: [
      {
        title: "E-Commerce Customer Segmentation",
        tech: "Python, Scikit-Learn",
        points: [
          "Applied K-means clustering algorithm to group 50,000+ active customers based on purchasing frequency.",
          "Designed personalized email marketing campaigns based on customer clusters, raising click-rate by 12%.",
        ],
      },
    ],
    languages: ["English (Native)", "Spanish (Conversational)"],
    certifications: ["Google Data Analytics Professional Certificate", "Tableau Desktop Certified Associate"],
  },

  // Estelle Darcy - Process Engineer
  modern_ats_pro: {
    name: "Estelle Darcy",
    role: "Process Engineer",
    email: "estelle.darcy@email.com",
    phone: "+1 (555) 382-9104",
    city: "Houston, TX",
    summary: "Detail-oriented Process Engineer with 4+ years of experience analyzing manufacturing workflows, designing automation systems, and driving operational efficiency improvements. Expert in Lean Manufacturing and Six Sigma methods.",
    skills: ["Process Simulation (ASPEN)", "AutoCAD", "Six Sigma Green Belt", "Lean Manufacturing", "Automation Systems", "HAZOP Analysis", "Root Cause Analysis"],
    experience: [
      {
        company: "Instrument Tech",
        role: "Process Engineer",
        duration: "Jan 2024 – Present",
        location: "Houston, TX",
        points: [
          "Led development of an advanced packaging automation system, achieving a 15% increase in plant operational efficiency.",
          "Streamlined manufacturing processes, reducing chemical waste by 10% and material costs by $50K annually.",
          "Implemented preventative maintenance strategies, resulting in a 20% decrease in plant equipment downtime.",
        ],
      },
      {
        company: "Arrowwai Industries",
        role: "Junior Process Associate",
        duration: "Jun 2022 – Aug 2022",
        location: "Austin, TX",
        points: [
          "Designed and optimized a robotic control system prototype, realizing a 12% performance improvement.",
          "Coordinated testing and validation procedures, ensuring full compliance with industry safety standards.",
        ],
      },
    ],
    education: [
      {
        college: "University of Engineering",
        degree: "Bachelor of Design in Process Engineering",
        year: "Sep 2019 – Sep 2023",
        cgpa: "8.9 CGPA",
      },
    ],
    projects: [
      {
        title: "Industrial Bio-Gas Extraction Pilot",
        tech: "ASPEN HYSYS, CAD",
        points: [
          "Modelled and scaled a bio-gas purification column, enhancing purity output by 14%.",
          "Presented technological advancements within the current Chemical & Process Industry conference.",
        ],
      },
    ],
    languages: ["English (Fluent)", "French (Intermediate)"],
    certifications: ["Professional Engineer (PE) License", "Project Management Tech (PMT)"],
  },

  // Arjun Sharma - Senior Software Engineer
  faang_engineering: {
    name: "Arjun Sharma",
    role: "Senior Software Engineer",
    email: "arjun.sharma@email.com",
    phone: "+91 98765 43210",
    city: "Bangalore, India",
    summary: "Results-driven Software Engineer with 5+ years building scalable web applications. Experienced in full-stack development with React, Node.js, and cloud infrastructure. Passionate about clean code and system performance.",
    skills: ["React", "TypeScript", "Node.js", "Python", "PostgreSQL", "Redis", "AWS", "Docker", "Kubernetes", "Git"],
    experience: [
      {
        company: "Infosys Limited",
        role: "Senior Software Engineer",
        duration: "Jan 2022 – Present",
        location: "Bangalore",
        points: [
          "Led development of a microservices platform serving 2M+ active users, reducing API latency by 40%.",
          "Architected React + TypeScript frontend that improved Core Web Vitals and page load speed by 55%.",
          "Mentored a team of 5 junior developers and conducted code reviews.",
          "Implemented CI/CD pipelines using GitHub Actions, cutting deployment time from 2 hours to 15 minutes.",
        ],
      },
      {
        company: "TCS Digital",
        role: "Software Engineer",
        duration: "Jun 2019 – Dec 2021",
        location: "Hyderabad",
        points: [
          "Built RESTful APIs in Node.js for a fintech platform processing ₹50Cr+ monthly transactions.",
          "Integrated third-party payment gateways (Razorpay, PayU) with 99.9% uptime.",
          "Reduced database query time by 60% through indexing and query optimization.",
        ],
      },
    ],
    education: [
      {
        college: "National Institute of Technology, Warangal",
        degree: "B.Tech in Computer Science",
        year: "2019",
        cgpa: "8.7 CGPA",
      },
    ],
    projects: [
      {
        title: "Distributed Task Scheduler",
        tech: "Go, Redis, Docker",
        points: [
          "Designed a fault-tolerant job scheduler queue managing 100k tasks per second.",
          "Implemented worker pool pattern with dynamic scale-up based on CPU utilisation.",
        ],
      },
    ],
    certifications: ["AWS Certified Solutions Architect", "Google Cloud Professional Developer"],
  },

  // Rachelle Beaudry - Accounting Executive
  executive_professional: {
    name: "Rachelle Beaudry",
    role: "Accounting Executive",
    email: "rachelle.beaudry@email.com",
    phone: "+1 (555) 789-0123",
    city: "Chicago, IL",
    summary: "Results-driven Accounting Executive with a proven record of optimizing financial performance. Expertise in strategic financial initiatives, audits, tax compliance, and team leadership. Seeking a challenging executive role to leverage analytical skills and drive organizational success.",
    skills: ["Financial Analysis", "Budgeting & Forecasting", "Risk Management", "Invoicing Systems", "Taxation Laws", "Accounting Software", "Data Analysis", "IFRS & GAAP"],
    experience: [
      {
        company: "Dorcells Accounting",
        role: "Accounting Executive",
        duration: "Jan 2023 - Present",
        location: "Chicago, IL",
        points: [
          "Implemented cost-control measures resulting in a 15% reduction in corporate operational expenses.",
          "Streamlined financial forecasting processes, improving forecasting accuracy by 20%.",
          "Led a team in successfully navigating a complex tax audit, ensuring full compliance with industry regulations.",
        ],
      },
      {
        company: "Salford & Co.",
        role: "Senior Accountant",
        duration: "Mar 2021 - Dec 2022",
        location: "Boston, MA",
        points: [
          "Managed month-end close processes, reducing closing time by 25%.",
          "Implemented a new financial software system, increasing productivity by 18%.",
          "Collaborated with cross-functional teams to develop and implement financial strategies, decreasing annual workload by 30%.",
        ],
      },
    ],
    education: [
      {
        college: "Kimberly University",
        degree: "Master of Professional Accounting",
        year: "Sep 2018 - Oct 2019",
        cgpa: "3.9 GPA",
      },
      {
        college: "City College",
        degree: "Bachelor of Accounting with Honours",
        year: "Aug 2015 - Aug 2018",
        cgpa: "3.8 GPA",
      },
    ],
    projects: [
      {
        title: "ERP Migration Implementation",
        tech: "SAP ERP, Financial Modules",
        points: [
          "Led the corporate migration of legacy accounting systems to SAP ERP for a team of 40 users.",
          "Secured zero-data loss during data migration and minimized operations downtime during transition.",
        ],
      },
    ],
    languages: ["English (Fluent)", "Malay (Fluent)", "Mandarin (Proficient)"],
    certifications: ["Certified Financial Analyst (CFA)", "Advanced Spreadsheet for Accounting & Finance"],
  },

  // Eez Abdelrahman - Mechanical Engineer
  campus_placement: {
    name: "Eez Abdelrahman",
    role: "Mechanical Engineer",
    email: "eez.abdelrahman@email.com",
    phone: "+1 (555) 456-7890",
    city: "San Francisco, CA",
    summary: "Results-oriented Mechanical and Mechatronics Engineer seeking a challenging position to apply expertise in designing and implementing innovative solutions for complex engineering challenges. Proven track record of success in project management, team collaboration, and operational efficiency.",
    skills: ["Thermodynamics", "SolidWorks", "MATLAB & Simulink", "Robotics", "Fluid Dynamics", "Rapid Prototyping", "Ansys FEA", "Python"],
    experience: [
      {
        company: "Borcelle Technologies",
        role: "Mechatronics Engineer",
        duration: "Jan 2023 - Present",
        location: "San Jose, CA",
        points: [
          "Led development of an advanced automation system, achieving a 15% increase in operational efficiency.",
          "Streamlined manufacturing processes, reducing production costs by 10%.",
          "Implemented preventative maintenance strategies, resulting in a 20% decrease in equipment downtime.",
        ],
      },
      {
        company: "Arrowwai Industries",
        role: "System Engineer",
        duration: "Feb 2021 - Dec 2022",
        location: "Austin, TX",
        points: [
          "Designed and optimized a robotic control system, realizing a 12% performance improvement.",
          "Coordinated testing and validation, ensuring compliance with industry standards.",
          "Provided technical expertise, contributing to a 15% reduction in system failures.",
        ],
      },
    ],
    education: [
      {
        college: "University of Excellence",
        degree: "Bachelor of Mechatronics Engineering with Honours",
        year: "Aug 2016 - Oct 2019",
        cgpa: "8.8 CGPA",
      },
    ],
    projects: [
      {
        title: "Autonomous Quadcopter Design",
        tech: "ROS, C++, SolidWorks",
        points: [
          "Created a drone prototype using ROS framework capable of obstacle avoidance in closed environments.",
          "Conducted stress analysis simulations on the main carbon-fibre frame using Ansys FEA.",
        ],
      },
    ],
    languages: ["English (Native)", "Arabic (Native)", "Japanese (Basic)"],
    certifications: ["Professional Engineer (PE) License", "Project Management Professional (PMP)"],
  },

  // Harper Russo - Web Developer
  internship_hunter: {
    name: "Harper Russo",
    role: "Web Developer",
    email: "harper.russo@email.com",
    phone: "+1 (555) 123-4567",
    city: "Seattle, WA",
    summary: "Creative and enthusiastic Web Developer with a strong passion for frontend design and interactive applications. Proven experience in HTML, CSS, JavaScript, and React through multiple successful personal and client projects.",
    skills: ["HTML5 & CSS3", "JavaScript (ES6)", "React.js", "Redux", "Tailwind CSS", "Git & GitHub", "REST APIs", "Responsive Web Design"],
    experience: [
      {
        company: "Freelance Client Project",
        role: "Frontend Developer",
        duration: "Jun 2023 – Oct 2023",
        location: "Remote",
        points: [
          "Built a responsive e-commerce web application, improving page load speeds by 30%.",
          "Integrated Stripe payment gateway APIs and managed user cart states with Redux.",
          "Developed custom CSS animations to enhance the digital storefront user experience.",
        ],
      },
    ],
    education: [
      {
        college: "Washington State University",
        degree: "B.S. in Software Engineering",
        year: "2020 - 2023",
        cgpa: "3.7 GPA",
      },
    ],
    projects: [
      {
        title: "Weather Dash - Live Analytics",
        tech: "React, OpenWeatherMap API",
        points: [
          "Designed a localized weather forecast application featuring interactive charts and metrics.",
          "Implemented geo-location tracking for automatic weather forecasts based on user position.",
        ],
      },
    ],
    languages: ["English (Native)", "German (Basic)"],
    certifications: ["Responsive Web Design (FreeCodeCamp)", "Advanced Frontend React Certificate"],
  },

  // Esther Balogun - Content Creator
  fresher_plus: {
    name: "Esther Balogun",
    role: "Content Creator & Digital Marketer",
    email: "esther.balogun@email.com",
    phone: "+1 (555) 901-2345",
    city: "Los Angeles, CA",
    summary: "Results-driven professional and creator with a strong background in operations and digital content strategy. Eager to leverage expertise in visual communication, community management, and operational efficiency to contribute to a dynamic team.",
    skills: ["Video Editing", "Content Strategy", "SEO Marketing", "Social Media Analytics", "Copywriting", "Email Campaigns", "Photoshop", "Brand Design"],
    experience: [
      {
        company: "Self-Employed / Remote",
        role: "Content Creator & Designer",
        duration: "April 2022 - Present",
        location: "Remote",
        points: [
          "Design and publish high-quality, ATS-friendly design templates for digital platforms.",
          "Produce technical tutorials for a global audience, focusing on productivity tools and faceless video production.",
          "Maintain platform-specific visual standards for all promotional assets to ensure maximum readability and engagement.",
        ],
      },
      {
        company: "Creative Lab",
        role: "Lead Organizer",
        duration: "November 2020 - March 2022",
        location: "Los Angeles, CA",
        points: [
          "Coordinated and hosted an in-person workshop for creators and tech enthusiasts.",
          "Facilitated hands-on design challenges, mentoring attendees on digital productivity.",
          "Managed event logistics, RSVP tracking, and community engagement.",
        ],
      },
    ],
    education: [
      {
        college: "California State University",
        degree: "B.A. in Digital Media & Communication",
        year: "2016 - 2020",
        cgpa: "3.8 GPA",
      },
    ],
    projects: [
      {
        title: "Faceless Creator Tutorial Hub",
        tech: "Canva, CapCut, YouTube Studio",
        points: [
          "Grew an educational video tutorials catalog to 10k subscribers with 500k+ total views.",
          "Optimized content descriptions and tags, leading to a 45% increase in organic search traffic.",
        ],
      },
    ],
    languages: ["English (Fluent)", "Yoruba (Conversational)"],
    certifications: ["Google Digital Garage - Digital Marketing", "YouTube Certified - Audience Growth"],
  },

  // Muhammad Patel - Marketing Manager
  finance_pro: {
    name: "Muhammad Patel",
    role: "Marketing Manager",
    email: "muhammad.patel@email.com",
    phone: "+1 (555) 456-7890",
    city: "Chicago, IL",
    summary: "Dynamic Marketing Manager with 6+ years of experience leading branding initiatives, digital advertising campaigns, and market research studies. Adept at managing client relationships and boosting online conversions.",
    skills: ["Brand Strategy", "Digital Advertising (AdWords, Meta)", "Market Research", "Conversion Optimization", "SEO & SEM", "CRM Tools (HubSpot)", "Analytics & Reporting"],
    experience: [
      {
        company: "Arowwai Industries",
        role: "Marketing Manager",
        duration: "2021 - Present",
        location: "Chicago, IL",
        points: [
          "Developed and executed search engine marketing campaigns, boosting website traffic by 40%.",
          "Managed a marketing budget of $120k annually, optimizing cost-per-click rates down by 15%.",
          "Led a branding project that refreshed corporate visual guidelines, increasing product recognition.",
        ],
      },
      {
        company: "Ginyard International Co.",
        role: "Product Marketing Executive",
        duration: "2018 - 2020",
        location: "Dallas, TX",
        points: [
          "Conducted audience research surveys to identify key segments, contributing to a 10% increase in product sales.",
          "Authored search-engine optimized copy for corporate websites and product landing pages.",
        ],
      },
    ],
    education: [
      {
        college: "Illinois Institute of Technology",
        degree: "M.S. in Marketing Analytics",
        year: "2016 - 2018",
        cgpa: "3.7 GPA",
      },
    ],
    projects: [
      {
        title: "Social Engagement Revamp",
        tech: "Google Analytics, HubSpot",
        points: [
          "Spearheaded a 6-month campaign to boost social media engagement, leading to a 50% increase in lead generation.",
          "Automated lead sorting pipelines via CRM tools, reducing response times by 24 hours.",
        ],
      },
    ],
    languages: ["English (Native)", "Hindi (Fluent)", "Gujarati (Fluent)"],
    certifications: ["HubSpot Inbound Marketing Certification", "Google Analytics Individual Qualification"],
  },

  // Aron Loeb - Graphics Designer
  product_consulting: {
    name: "Aron Loeb",
    role: "Graphics Designer",
    email: "aron.loeb@email.com",
    phone: "+1 (555) 234-5678",
    city: "San Francisco, CA",
    summary: "Creative Graphic Designer with 5+ years of experience designing brand identities, marketing assets, and digital illustrations. Dedicated to craft visual stories that resonate with target audiences and drive customer engagement.",
    skills: ["Adobe Creative Suite", "Brand Identity Design", "Illustrations", "UI/UX Design", "Typography", "Print Layouts", "Motion Graphics"],
    experience: [
      {
        company: "Really Great Company",
        role: "Graphic Designer",
        duration: "2022 - Present",
        location: "San Francisco, CA",
        points: [
          "Redesigned the visual brand identity for 10+ corporate clients, increasing brand consistency across channels.",
          "Created digital marketing assets for social media and display ads, increasing overall click-through rates by 12%.",
          "Collaborated with product teams to design web interface concepts and layout templates.",
        ],
      },
      {
        company: "Creative Studio",
        role: "Junior Designer",
        duration: "2019 - 2021",
        location: "Oakland, CA",
        points: [
          "Designed print marketing materials including brochures, banners, and corporate presentation packages.",
          "Retouched high-volume product photos for an e-commerce website portal, improving visual standard uniformity.",
        ],
      },
    ],
    education: [
      {
        college: "California College of the Arts",
        degree: "Bachelor of Fine Arts in Graphic Design",
        year: "2015 - 2019",
        cgpa: "3.9 GPA",
      },
    ],
    projects: [
      {
        title: "Brand Identity - EcoLife",
        tech: "Adobe Illustrator, Photoshop",
        points: [
          "Created a complete brand book including logos, typography guides, and packaging designs for an eco-friendly brand.",
          "Designed and delivered a set of 50+ custom icons for the client's mobile application.",
        ],
      },
    ],
    languages: ["English (Fluent)", "German (Conversational)"],
    certifications: ["Adobe Certified Professional - Illustrator", "Design Ethics & Copyright Law"],
  },

  // Eez Abdelrahman - Mechanical Engineer (sidebar template)
  creative_professional: {
    name: "Aron Loeb",
    role: "Graphics Designer",
    email: "aron.loeb@email.com",
    phone: "+1 (555) 234-5678",
    city: "San Francisco, CA",
    summary: "Creative Graphic Designer with 5+ years of experience designing brand identities, marketing assets, and digital illustrations. Dedicated to craft visual stories that resonate with target audiences and drive customer engagement.",
    skills: ["Adobe Creative Suite", "Brand Identity Design", "Illustrations", "UI/UX Design", "Typography", "Print Layouts", "Motion Graphics"],
    experience: [
      {
        company: "Really Great Company",
        role: "Graphic Designer",
        duration: "2022 - Present",
        location: "San Francisco, CA",
        points: [
          "Redesigned the visual brand identity for 10+ corporate clients, increasing brand consistency across channels.",
          "Created digital marketing assets for social media and display ads, increasing overall click-through rates by 12%.",
          "Collaborated with product teams to design web interface concepts and layout templates.",
        ],
      },
      {
        company: "Creative Studio",
        role: "Junior Designer",
        duration: "2019 - 2021",
        location: "Oakland, CA",
        points: [
          "Designed print marketing materials including brochures, banners, and corporate presentation packages.",
          "Retouched high-volume product photos for an e-commerce website portal, improving visual standard uniformity.",
        ],
      },
    ],
    education: [
      {
        college: "California College of the Arts",
        degree: "BFA in Graphic Design",
        year: "2015 - 2019",
        cgpa: "3.9 GPA",
      },
    ],
    projects: [
      {
        title: "Brand Identity - EcoLife",
        tech: "Illustrator, Photoshop",
        points: [
          "Created a complete brand book including logos, typography guides, and packaging designs for an eco-friendly brand.",
          "Designed and delivered a set of 50+ custom icons for the client's mobile application.",
        ],
      },
    ],
    languages: ["English (Fluent)", "German (Conversational)"],
    certifications: ["Adobe Certified Professional", "Design Ethics Law"],
  },

  // Priya Menon - DevOps Engineer
  clean_minimal_ats: {
    name: "Priya Menon",
    role: "DevOps Engineer",
    email: "priya.menon@email.com",
    phone: "+91 98765 12345",
    city: "Pune, India",
    summary: "DevOps Engineer with 4+ years of experience in CI/CD automation, cloud infrastructure, and container orchestration. Passionate about building reliable, scalable deployment pipelines and reducing operational overhead.",
    skills: ["AWS", "Docker", "Kubernetes", "Terraform", "Jenkins", "GitHub Actions", "Linux", "Python", "Monitoring (Grafana, Prometheus)", "Ansible"],
    experience: [
      {
        company: "Wipro Technologies",
        role: "Senior DevOps Engineer",
        duration: "Jan 2022 – Present",
        location: "Pune",
        points: [
          "Architected multi-region Kubernetes clusters on AWS EKS serving 5M+ daily requests with 99.95% uptime.",
          "Built automated CI/CD pipelines using Jenkins & GitHub Actions, reducing deployment time by 70%.",
          "Implemented Infrastructure-as-Code with Terraform, managing 200+ cloud resources across 3 AWS accounts.",
        ],
      },
      {
        company: "Tech Mahindra",
        role: "DevOps Engineer",
        duration: "Jul 2019 – Dec 2021",
        location: "Hyderabad",
        points: [
          "Containerized 15+ microservices using Docker, improving development environment consistency.",
          "Set up monitoring dashboards with Grafana & Prometheus, reducing incident detection time by 60%.",
        ],
      },
    ],
    education: [
      {
        college: "Pune Institute of Engineering",
        degree: "B.Tech in Information Technology",
        year: "2015 - 2019",
        cgpa: "8.5 CGPA",
      },
    ],
    projects: [
      {
        title: "Auto-Scaling Pipeline Engine",
        tech: "Kubernetes, HPA, Prometheus",
        points: [
          "Designed auto-scaling policies based on custom metrics, handling 3x traffic spikes without manual intervention.",
          "Reduced cloud compute costs by 35% through right-sizing and spot instance utilization.",
        ],
      },
    ],
    languages: ["English (Fluent)", "Hindi (Native)", "Marathi (Native)"],
    certifications: ["AWS Certified DevOps Engineer – Professional", "Certified Kubernetes Administrator (CKA)"],
  },

  // Daniel Park - Business Analyst
  two_column_ats: {
    name: "Daniel Park",
    role: "Business Analyst",
    email: "daniel.park@email.com",
    phone: "+1 (555) 678-9012",
    city: "Austin, TX",
    summary: "Data-driven Business Analyst with 5+ years of experience translating business requirements into actionable insights. Expert in SQL, data visualization, and cross-functional stakeholder management.",
    skills: ["SQL (MySQL, PostgreSQL)", "Tableau", "Power BI", "JIRA", "Confluence", "Python (Pandas)", "Agile / Scrum", "Stakeholder Management", "Requirements Analysis", "Excel (Advanced)"],
    experience: [
      {
        company: "Dell Technologies",
        role: "Senior Business Analyst",
        duration: "Mar 2021 – Present",
        location: "Austin, TX",
        points: [
          "Led requirements gathering for a $2M CRM migration project, reducing go-live timeline by 3 weeks.",
          "Created executive dashboards in Tableau tracking KPIs across 5 business units with 200+ users.",
          "Facilitated Agile ceremonies for a 12-member cross-functional team, improving sprint velocity by 25%.",
        ],
      },
      {
        company: "Accenture",
        role: "Business Analyst",
        duration: "Jun 2018 – Feb 2021",
        location: "Dallas, TX",
        points: [
          "Documented 50+ business process flows using BPMN notation for a healthcare client.",
          "Performed gap analysis between legacy and target systems, identifying 30+ integration points.",
        ],
      },
    ],
    education: [
      {
        college: "University of Texas at Austin",
        degree: "M.S. in Business Analytics",
        year: "2016 - 2018",
        cgpa: "3.8 GPA",
      },
      {
        college: "Seoul National University",
        degree: "B.S. in Economics",
        year: "2012 - 2016",
        cgpa: "3.7 GPA",
      },
    ],
    projects: [
      {
        title: "Customer Churn Prediction Model",
        tech: "Python, Scikit-Learn, Tableau",
        points: [
          "Built a logistic regression model predicting customer churn with 87% accuracy across 100K records.",
          "Designed an interactive Tableau dashboard for the retention team, enabling proactive outreach.",
        ],
      },
    ],
    languages: ["English (Native)", "Korean (Fluent)"],
    certifications: ["Certified Business Analysis Professional (CBAP)", "Tableau Desktop Specialist"],
  },
};
